import clsx from 'clsx';

/**
 * StatusBadge - Durum göstergesi
 * 
 * @param {string} status - Durum key'i
 * @param {string} label - Gösterilecek metin
 * @param {string} color - Renk (primary, success, warning, error, info)
 */
export function StatusBadge({ status, label, color = 'primary' }) {
    const colorClasses = {
        primary: 'bg-primary/10 text-primary',
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/10 text-warning',
        error: 'bg-error/10 text-error',
        info: 'bg-info/10 text-info',
        neutral: 'bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
    };

    return (
        <span className={clsx(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
            colorClasses[color] || colorClasses.primary
        )}>
            {label || status}
        </span>
    );
}

/**
 * StockIndicator - Stok durumu göstergesi
 * 
 * @param {number} current - Mevcut stok
 * @param {number} total - Toplam miktar
 * @param {string} birim - Birim (adet, m², ton, vb.)
 * @param {boolean} showBar - İlerleme çubuğu göster
 */
export function StockIndicator({ current = 0, total = 0, birim = '', showBar = true }) {
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

    // Stok durumu rengi
    let colorClass = 'bg-success';
    let textColorClass = 'text-success';
    if (percentage <= 10) {
        colorClass = 'bg-error';
        textColorClass = 'text-error';
    } else if (percentage <= 30) {
        colorClass = 'bg-warning';
        textColorClass = 'text-warning';
    }

    return (
        <div className="space-y-1">
            <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Stok:</span>
                <span className={clsx('font-medium', textColorClass)}>
                    {current.toLocaleString('tr-TR')} {birim}
                </span>
            </div>
            {showBar && (
                <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden">
                    <div
                        className={clsx('h-full rounded-full transition-all', colorClass)}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                </div>
            )}
            {total > 0 && (
                <p className="text-xs text-text-muted">
                    {total.toLocaleString('tr-TR')} {birim} alındı, {(total - current).toLocaleString('tr-TR')} kullanıldı
                </p>
            )}
        </div>
    );
}

/**
 * ProgressIndicator - İlerleme göstergesi
 * 
 * @param {number} completed - Tamamlanan
 * @param {number} total - Toplam
 * @param {string} label - Etiket
 */
export function ProgressIndicator({ completed = 0, total = 0, label = 'İlerleme' }) {
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    let colorClass = 'bg-warning';
    if (percentage >= 100) {
        colorClass = 'bg-success';
    } else if (percentage < 25) {
        colorClass = 'bg-info';
    }

    return (
        <div className="space-y-1">
            <div className="flex justify-between text-sm">
                <span className="text-text-secondary">{label}:</span>
                <span className="font-medium text-text-primary">%{percentage}</span>
            </div>
            <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden">
                <div
                    className={clsx('h-full rounded-full transition-all', colorClass)}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                />
            </div>
            <p className="text-xs text-text-muted">
                {completed.toLocaleString('tr-TR')} / {total.toLocaleString('tr-TR')}
            </p>
        </div>
    );
}

/**
 * Durum helper - duruma göre badge döndür
 */
export function getDurumBadge(durum) {
    const durumlar = {
        teslim: { label: 'Teslim Edildi', color: 'success' },
        bekliyor: { label: 'Bekliyor', color: 'warning' },
        iptal: { label: 'İptal', color: 'error' },
        kismen: { label: 'Kısmi Teslim', color: 'info' },
        devam: { label: 'Devam Ediyor', color: 'info' },
        tamamlandi: { label: 'Tamamlandı', color: 'success' },
        planlandi: { label: 'Planlandı', color: 'neutral' },
        onaylandi: { label: 'Onaylandı', color: 'success' },
        reddedildi: { label: 'Reddedildi', color: 'error' },
    };

    const config = durumlar[durum] || { label: durum, color: 'neutral' };
    return <StatusBadge status={durum} label={config.label} color={config.color} />;
}

/**
 * Para formatı helper
 */
export function formatCurrency(amount) {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

/**
 * Tarih formatı helper
 */
export function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}
